// src/component/ui/PageHeaders.jsx

import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled } from '@mui/material/styles';

const BackButton = styled(IconButton)(({ theme }) => ({
  marginRight: '12px',
  border: '1px solid',
  borderColor: theme.palette.divider,
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

/**
 * PageHeader - Top-level page header component
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} [props.subtitle] - Supporting descriptive text
 * @param {function} [props.onBack] - If provided, displays an accessible back button
 * @param {React.ReactNode} [props.action] - Right-aligned action button/elements
 */
export const PageHeader = ({ title, subtitle, onBack, action }) => {
  return (
    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
      <Box style={{ display: 'flex', alignItems: 'center' }}>
        {onBack && (
          <BackButton onClick={onBack} aria-label="Go back" size="small">
            <ArrowBackIcon fontSize="small" />
          </BackButton>
        )}
        <Box>
          <Typography variant="h1" component="h1" style={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" color="textSecondary" style={{ marginTop: '4px' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {action && (
        <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {action}
        </Box>
      )}
    </Box>
  );
};

/**
 * SectionHeader - Header for nested page sections or grids
 * @param {Object} props
 * @param {string} props.title - Section title
 * @param {React.ReactNode} [props.action] - Optional right-aligned action elements
 */
export const SectionHeader = ({ title, action }) => {
  return (
    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', marginTop: '16px' }}>
      <Typography variant="h2" component="h2">
        {title}
      </Typography>
      {action && (
        <Box>{action}</Box>
      )}
    </Box>
  );
};


